/**
 * Generic stage tab: search, filters placeholder, sortable table with configurable columns and row actions.
 */

import {
  ActionIcon,
  Anchor,
  Autocomplete,
  Badge,
  Box,
  Button,
  Group,
  Menu,
  Pagination,
  Select,
  Table,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconDots, IconFilter, IconLayoutColumns } from '@tabler/icons-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { LifecycleStage } from '../types/lifecycle';
import {
  STAGE_COLUMN_INFO,
  STAGE_COLUMNS,
  STAGE_HAS_ACTION,
  STAGE_LABELS,
  getAllTrackerColumns,
  getCancelReasonLabel,
  getFulfilledStatusLabel,
  getWaitlistReasonLabel,
  type TrackerColumn,
} from '../data/stageConfig';
import { FilterListDrawer, type FilterListSection } from '../../../components/FilterListDrawer';
import { ColumnSelectModal } from './ColumnSelectModal';
import { SearchInput } from '../../../components/SearchInput';
import { useEpisodeOfCareTypes } from '../../calendar/episodeOfCareTypeCatalog';
import { PresenceAvatars } from '@/components/presence/PresenceAvatars';
import type { PresenceActor } from '@/lib/presence/presenceFhir';
import { presenceRowKey } from '@/lib/presence/presenceFhir';
import { formatUsDateTime } from '@/lib/dateHelpers';

const STATE_FILTER_OPTIONS = [
  { value: 'UT', label: 'Utah' },
  { value: 'CA', label: 'California' },
] as const;

const ROWS_PER_PAGE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
];

/** Row height (px) for spacer below table so partial pages match full-page height (Mantine Table default ~44). */
const TABLE_ROW_HEIGHT_PX = 44;

/** Maps filter section labels to the row field keys they should match against. */
const FILTER_KEY_MAP: Record<string, string[]> = {
  'Service Type': ['episodeServiceType'],
  State: ['state'],
};

function matchesFilters(row: StageRow, activeFilters: Record<string, string[]>): boolean {
  for (const [section, selected] of Object.entries(activeFilters)) {
    if (selected.length === 0) continue;
    const keys = FILTER_KEY_MAP[section];
    if (!keys) continue;
    const match = keys.some((key) => {
      const val = row[key];
      if (val == null) return false;
      if (typeof val === 'string') return selected.includes(val);
      if (Array.isArray(val)) return val.some((v) => selected.includes(String(v)));
      return selected.includes(String(val));
    });
    if (!match) return false;
  }
  return true;
}

export type StageRow = Record<string, unknown>;

type SortDir = 'asc' | 'desc';

function getSortValue(row: StageRow, key: string): string | number | null {
  const v = row[key];
  if (v == null) return null;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return new Date(v).getTime();
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

function compareRows(a: StageRow, b: StageRow, key: string, dir: SortDir): number {
  const va = getSortValue(a, key);
  const vb = getSortValue(b, key);
  const nullFirst = 1;
  if (va == null && vb == null) return 0;
  if (va == null) return dir === 'asc' ? -nullFirst : nullFirst;
  if (vb == null) return dir === 'asc' ? nullFirst : -nullFirst;
  let cmp = 0;
  if (typeof va === 'number' && typeof vb === 'number') {
    cmp = va - vb;
  } else {
    cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
  }
  return dir === 'asc' ? cmp : -cmp;
}

const STAGE_BADGE_COLOR: Record<LifecycleStage, string> = {
  'new-client': 'gray',
  'needs-verification': 'orange',
  waitlist: 'yellow',
  screening: 'blue',
  matching: 'violet',
  consultation: 'cyan',
  active: 'green',
  transfer: 'indigo',
  inactive: 'gray',
  cancelled: 'red',
  fulfilled: 'teal',
  all: 'gray',
};

const US_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'numeric',
  day: 'numeric',
  year: 'numeric',
};

function formatCellValue(key: string, value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (key === 'submissionDate' && typeof value === 'string') {
    const formatted = formatUsDateTime(value);
    return formatted || String(value);
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    try {
      return new Date(value).toLocaleDateString('en-US', US_DATE_OPTIONS);
    } catch {
      return String(value);
    }
  }
  if (key === 'waitlistReason') return getWaitlistReasonLabel(String(value));
  if (key === 'cancelReason') return getCancelReasonLabel(String(value));
  if (key === 'fulfilledStatus') return getFulfilledStatusLabel(String(value));
  return String(value);
}

function ColumnHeaderLabel({ col }: { col: TrackerColumn }): JSX.Element {
  return (
    <Text size="sm" fw={600} component="span" lineClamp={2}>
      {col.displayLabel}
    </Text>
  );
}

export interface StageTabProps {
  stage: LifecycleStage;
  rows: StageRow[];
  /** Practice names for the Practice filter dropdown, derived from real data. */
  practiceOptions?: string[];
  /** Live presence bubbles keyed by row id (ServiceRequest.id). */
  presenceByRow?: Map<string, PresenceActor[]>;
  onRowClick?: (row: StageRow) => void;
  onOpenMatchingPanel?: (row: StageRow) => void;
  /** Called for any row action (e.g. Needs Verification: View Error Details, Manually Enter Benefits). */
  onRowAction?: (action: string, row: StageRow) => void;
  /** Called when user clicks the single contextual Action button (opens stage-specific modal). */
  onContextualAction?: (row: StageRow) => void;
  /** Screening stage only: opens the read-only high-risk screening results modal for the row. */
  onViewScreeningResults?: (row: StageRow) => void;
  /** Screening stage only: opens the modal to schedule a high-risk screening call for the row. */
  onScheduleCall?: (row: StageRow) => void;
  /** Screening stage only: marks the client as uninterested (placeholder — wired later). */
  onMarkUninterested?: (row: StageRow) => void;
  /** Matching stage only: opens the manual-assign modal for the row. */
  onManualAssign?: (row: StageRow) => void;
  /** Transfer stage only: opens the read-only transfer request modal for the row. */
  onViewTransferRequest?: (row: StageRow) => void;
  /** Fulfilled stage only: opens the transfer request form for an ongoing episode. */
  onInitiateTransfer?: (row: StageRow) => void;
}

export function StageTab({
  stage,
  rows,
  practiceOptions,
  presenceByRow,
  onRowClick,
  onOpenMatchingPanel: _onOpenMatchingPanel,
  onRowAction: _onRowAction,
  onContextualAction,
  onViewScreeningResults,
  onScheduleCall,
  onMarkUninterested,
  onManualAssign,
  onViewTransferRequest,
  onInitiateTransfer,
}: StageTabProps): JSX.Element {
  const { types: episodeOfCareTypes } = useEpisodeOfCareTypes();
  const [search, setSearch] = useState('');
  const [practice, setPractice] = useState<string | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const storageKey = `tracker-columns-${stage}`;
  const [visibleColumnOrder, setVisibleColumnOrder] = useState<string[] | null>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState('10');
  const [measuredRowHeightPx, setMeasuredRowHeightPx] = useState<number | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(`tracker-columns-${stage}`);
      setVisibleColumnOrder(stored ? JSON.parse(stored) : null);
    } catch {
      setVisibleColumnOrder(null);
    }
    setPage(1);
  }, [stage]);

  const filterSections = useMemo<FilterListSection[]>(
    () => [
      {
        label: 'Service Type',
        options: episodeOfCareTypes.map((t) => ({ value: t.name, label: t.name })),
      },
      {
        label: 'State',
        options: STATE_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      },
    ],
    [episodeOfCareTypes],
  );

  const activeFilterCount = useMemo(() => Object.values(activeFilters).reduce((sum, v) => sum + v.length, 0), [activeFilters]);

  const columns = STAGE_COLUMNS[stage] ?? [];
  const showScreeningMenu = stage === 'screening';
  const showMatchingMenu = stage === 'matching';
  const showTransferMenu = stage === 'transfer';
  const showFulfilledMenu = stage === 'fulfilled';
  const showActionButton =
    !showScreeningMenu && !showMatchingMenu && !showTransferMenu && !showFulfilledMenu && stage !== 'all' && (STAGE_HAS_ACTION[stage] ?? false);
  // Trailing column always renders so live presence bubbles can appear on every tab,
  // alongside any stage-specific action control.
  const actionColWidth = showActionButton ? 140 : 96;
  const allColumns = useMemo(
    () =>
      getAllTrackerColumns().map((column) => ({
        ...column,
        description: STAGE_COLUMN_INFO[column.key],
      })),
    [],
  );

  const columnKeyToConfig = useMemo(() => {
    const map = new Map<string, TrackerColumn>();
    for (const c of allColumns) map.set(c.key, c);
    return map;
  }, [allColumns]);

  const visibleColumns = useMemo(() => {
    const keys = visibleColumnOrder == null || visibleColumnOrder.length === 0
      ? columns.map((c) => c.key)
      : visibleColumnOrder;
    return keys
      .map((key) => columnKeyToConfig.get(key))
      .filter((c): c is TrackerColumn => c != null);
  }, [columns, visibleColumnOrder, columnKeyToConfig]);

  const filteredRows = useMemo(() => {
    let result = rows;

    if (practice) {
      result = result.filter((row) => row.practice === practice);
    }

    if (activeFilterCount > 0) {
      result = result.filter((row) => matchesFilters(row, activeFilters));
    }

    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter((row) =>
        Object.values(row).some((v) => (typeof v === 'string' && v.toLowerCase().includes(s)) || (Array.isArray(v) && v.some((x) => String(x).toLowerCase().includes(s))))
      );
    }

    return result;
  }, [rows, search, practice, activeFilters, activeFilterCount]);

  const sortedRows = useMemo(() => {
    if (!sortKey || !visibleColumns.some((c) => c.key === sortKey && c.sortable)) return filteredRows;
    return [...filteredRows].sort((a, b) => compareRows(a, b, sortKey, sortDir));
  }, [filteredRows, sortKey, sortDir, visibleColumns]);

  const itemsPerPage = parseInt(rowsPerPage || '10', 10);
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / itemsPerPage));
  const safePage = Math.min(Math.max(1, page), totalPages);
  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);
  const paginatedRows = useMemo(
    () => sortedRows.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage),
    [sortedRows, safePage, itemsPerPage]
  );
  const rangeStart = sortedRows.length === 0 ? 0 : (safePage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(safePage * itemsPerPage, sortedRows.length);

  useLayoutEffect(() => {
    if (paginatedRows.length === 0) return;
    const firstRow = tableWrapperRef.current?.querySelector('tbody tr');
    if (firstRow) {
      const h = firstRow.getBoundingClientRect().height;
      setMeasuredRowHeightPx(Math.round(h));
    }
  }, [paginatedRows.length, stage, visibleColumns.length]);

  const handleSort = (key: string): void => {
    if (sortKey === key) {
      if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        setSortKey(null);
      }
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <>
      <Group mb="md" gap="sm">
        <SearchInput
          placeholder="Search by name, email, payer..."
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          w={280}
        />
        <Autocomplete
          placeholder="Practice"
          data={practiceOptions ?? []}
          value={practice ?? ''}
          onChange={(value) => setPractice(value.trim() || null)}
          onOptionSubmit={(value) => setPractice(value || null)}
          clearable
          style={{ width: 200 }}
        />
        <Button
          variant="outline"
          leftSection={<IconFilter size={16} />}
          onClick={() => setFilterDrawerOpen(true)}
        >
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </Button>
        <Button variant="outline" leftSection={<IconLayoutColumns size={16} />} onClick={() => setColumnModalOpen(true)}>
          Columns
        </Button>
      </Group>

      <ColumnSelectModal
        opened={columnModalOpen}
        onClose={() => setColumnModalOpen(false)}
        allColumns={allColumns}
        defaultColumnKeys={columns.map((c) => c.key)}
        value={visibleColumnOrder}
        centered
        alwaysShowScrollbars
        onApply={(v) => {
          setVisibleColumnOrder(v);
          try {
            if (v) {
              localStorage.setItem(storageKey, JSON.stringify(v));
            } else {
              localStorage.removeItem(storageKey);
            }
          } catch { /* quota exceeded or private browsing */ }
          setColumnModalOpen(false);
        }}
      />

      <FilterListDrawer
        opened={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title="Filters"
        sections={filterSections}
        size="md"
        onApply={(selections) => {
          setActiveFilters(selections);
          setFilterDrawerOpen(false);
        }}
        onClear={() => setActiveFilters({})}
      />

      <Box
        ref={tableWrapperRef}
        style={{ overflowX: 'scroll', scrollbarGutter: 'stable' }}
      >
        <Table
          highlightOnHover
          verticalSpacing="sm"
          horizontalSpacing="sm"
          style={{ tableLayout: 'fixed', width: '100%' }}
        >
          <Table.Thead>
            <Table.Tr>
              {visibleColumns.map((col) => (
                <Table.Th key={col.key} style={{ verticalAlign: 'middle' }}>
                  {col.sortable !== false ? (
                    <UnstyledButton
                      onClick={() => handleSort(col.key)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', maxWidth: '100%' }}
                    >
                      <ColumnHeaderLabel col={col} />
                      {sortKey === col.key ? (
                        sortDir === 'asc' ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />
                      ) : null}
                    </UnstyledButton>
                  ) : (
                    <ColumnHeaderLabel col={col} />
                  )}
                </Table.Th>
              ))}
              <Table.Th w={actionColWidth} miw={actionColWidth} style={{ verticalAlign: 'middle', textAlign: 'right' }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {paginatedRows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={visibleColumns.length + 1}>
                  <Text size="sm" c="dimmed" py="xl" ta="center">
                    No {STAGE_LABELS[stage].toLowerCase()} records
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              paginatedRows.map((row) => (
                <Table.Tr
                  key={String(row.id)}
                  style={{ cursor: onRowClick ? 'pointer' : undefined }}
                  onClick={() => onRowClick?.(row)}
                >
                  {visibleColumns.map((col) => {
                    const raw = row[col.key];
                    const stageCode = col.key === 'stage' && typeof raw === 'string' ? (raw as LifecycleStage) : null;
                    const display = stageCode
                      ? STAGE_LABELS[stageCode] ?? raw
                      : formatCellValue(col.key, raw);
                    const isBadge = col.key === 'verificationStatus' || col.key === 'screeningOutcome' || col.key === 'meetingStatus' || col.key === 'transferStatus' || col.key === 'cancelReason';
                    const clientId = row.clientId ?? row.id;
                    const isClientName = col.key === 'clientName' && clientId != null;
                    const isEpisodeServiceType = col.key === 'episodeServiceType' && clientId != null && row.episodeOfCareId != null;
                    return (
                      <Table.Td key={col.key} style={{ verticalAlign: 'middle', wordBreak: 'break-word' }}>
                        {stageCode ? (
                          <Badge size="sm" variant="light" color={STAGE_BADGE_COLOR[stageCode] ?? 'gray'}>
                            {display}
                          </Badge>
                        ) : isClientName ? (
                          <Anchor
                            component={Link}
                            to={`/dashboard/clients/${clientId}`}
                            fw={500}
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                            lineClamp={3}
                          >
                            {display}
                          </Anchor>
                        ) : isEpisodeServiceType ? (
                          <Anchor
                            component={Link}
                            to={`/dashboard/clients/${clientId}/episode-of-care/${row.episodeOfCareId}`}
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                            lineClamp={3}
                          >
                            {display}
                          </Anchor>
                        ) : isBadge && typeof display === 'string' && display !== '—' ? (
                          <Badge size="sm" variant="light">
                            {display}
                          </Badge>
                        ) : (
                          <Text size="sm" component="span" style={{ wordBreak: 'break-word' }} lineClamp={4}>
                            {display}
                          </Text>
                        )}
                      </Table.Td>
                    );
                  })}
                  <Table.Td
                    onClick={(e) => e.stopPropagation()}
                    style={{ verticalAlign: 'middle', textAlign: 'right', width: actionColWidth }}
                  >
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <PresenceAvatars actors={presenceByRow?.get(presenceRowKey('tracker', String(row.id))) ?? []} />
                      {showScreeningMenu ? (
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" aria-label="Row actions">
                              <IconDots size={18} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item onClick={() => onViewScreeningResults?.(row)}>
                              View Screening
                            </Menu.Item>
                            <Menu.Item
                              disabled={Boolean(row.hasScreeningCall)}
                              onClick={() => onScheduleCall?.(row)}
                            >
                              Schedule Call
                            </Menu.Item>
                            <Menu.Item onClick={() => onMarkUninterested?.(row)}>
                              Mark As Uninterested
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      ) : showMatchingMenu ? (
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" aria-label="Row actions">
                              <IconDots size={18} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item onClick={() => onContextualAction?.(row)}>
                              Matching Tool
                            </Menu.Item>
                            <Menu.Item onClick={() => onManualAssign?.(row)}>
                              Manual Assign
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      ) : showTransferMenu ? (
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" aria-label="Row actions">
                              <IconDots size={18} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item onClick={() => onViewTransferRequest?.(row)}>
                              View transfer request
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      ) : showFulfilledMenu && row.fulfilledStatus === 'ongoing' ? (
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" aria-label="Row actions">
                              <IconDots size={18} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item onClick={() => onInitiateTransfer?.(row)}>
                              Initiate Transfer
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      ) : showActionButton ? (
                        <Button variant="subtle" size="xs" onClick={() => onContextualAction?.(row)}>
                          Action
                        </Button>
                      ) : null}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Box>

      {/* Spacer so table + padding equals full-page height when page is partial (no lines, actual padding) */}
      {paginatedRows.length > 0 && paginatedRows.length < itemsPerPage && (
        <Box
          style={{
            height: (itemsPerPage - paginatedRows.length) * (measuredRowHeightPx ?? TABLE_ROW_HEIGHT_PX),
          }}
          aria-hidden
        />
      )}

      {/* Pagination — matches theming page: size="xs", withEdges from theme */}
      <Group justify="space-between" mt="md">
        <Group gap="xs">
          <Text size="sm" c="dimmed">
            Rows per page:
          </Text>
          <Select
            value={rowsPerPage}
            onChange={(value) => {
              setRowsPerPage(value ?? '10');
              setPage(1);
            }}
            data={ROWS_PER_PAGE_OPTIONS}
            w={80}
            size="xs"
          />
          <Text size="sm" c="dimmed">
            {sortedRows.length === 0 ? '0-0 of 0' : `${rangeStart}-${rangeEnd} of ${sortedRows.length}`}
          </Text>
        </Group>
        <Pagination
          total={totalPages}
          value={safePage}
          onChange={setPage}
          size="xs"
          aria-label="Table pagination"
        />
      </Group>
    </>
  );
}
